import BaseModal from '@components/ui/BaseModal';
import LastLocation from '@components/ui/Location/LastLocation';
import SkeletonOriginAndDestination from '@components/ui/Skeleton/SkeletonOriginAndDestination';
import SkeletonRow from '@components/ui/Skeleton/SkeletonRow';
import TruckerInfo from '@components/ui/Trucker/TruckerInfo';
import AppContext from '@context';
import {
  FuelButton,
  FuelCallout,
  FuelSkeleton,
  FuelTypography,
} from '@frete.com/fuel-react';
import {
  FuelFlex,
  FuelOriginAndDestination,
} from '@frete.com/fuel-station-react';
import { pageLinkOpen } from '@helpers/journeys/pageLinkOpen';
import useAmplitude from '@hooks/useAmplitude';
import { useTimeout } from '@hooks/useTimeout';
import useUpdateProposal from '@hooks/useUpdateProposal';
import {
  ProposalPendingInterface,
  ProposalTruckerInterface,
} from '@interface/proposals';
import React, { useContext, useEffect, useRef } from 'react';
import Actions from './Actions';
import * as Styled from './DrawerProposalStyles';
import { DrawerProposalPropsInterface } from './DrawerProposalTypes';

function DrawerProposal({ isMobile }: DrawerProposalPropsInterface) {
  const baseRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLDivElement>(null);

  const addTimeout = useTimeout();
  const {
    state: { ui, proposals },
    dispatch,
  } = useContext(AppContext);
  const { amplitudeEvent } = useAmplitude();

  const { isShow, proposalId } = ui.DrawerProposal;
  const { fetchProposal, isloadingProposal } = useUpdateProposal({
    proposalId,
  });
  const proposalsSelection = proposals?.selection as ProposalPendingInterface;
  const isloading = isloadingProposal();
  let trucker: ProposalTruckerInterface | null = null;

  if (proposalsSelection?.trucker) {
    trucker = proposalsSelection.trucker;
  }

  function onClose() {
    return new Promise<void>((resolve) => {
      baseRef.current?.classList.remove('animated');
      contentRef.current?.classList.remove('animated');
      actionRef.current?.classList.remove('animated');
      addTimeout(() => {
        resolve();
      }, 200);
    });
  }

  function handleClose() {
    onClose().then(() => {
      dispatch({
        type: 'ui_drawerProposal_set_show',
        payload: { isShow: false, proposalId: '' },
      });
      dispatch({ type: 'proposal_selection_clear' });
    });
    amplitudeEvent('drawer_proposal_pending_closed', {
      source: 'fechar-drawer-proposta-pendente',
      proposal_id: proposalsSelection?.id,
    });
  }

  function handleTruckerInfo(loading: boolean) {
    if (loading) {
      return (
        <Styled.Section>
          <FuelFlex defaultProps={{ alignItems: 'flex-start', gap: 'km3' }}>
            <FuelSkeleton
              shape="circle"
              size="custom"
              width="40px"
              height="40px"
            />
            <Styled.ContentSkeleton>
              <SkeletonRow width="108px" height="18px" />
              <SkeletonRow width="108px" height="20px" />
            </Styled.ContentSkeleton>
            <FuelFlex defaultProps={{ gap: 'km2', customMargin: '0 0 0 auto' }}>
              <SkeletonRow width="32px" height="32px" />
              <SkeletonRow width="154px" height="32px" />
            </FuelFlex>
          </FuelFlex>
        </Styled.Section>
      );
    }
    if (!trucker?.name) {
      return null;
    }

    return (
      <Styled.Section>
        <TruckerInfo
          avatar={trucker?.avatar || ''}
          name={trucker?.name || ''}
          phone={{
            number: trucker.phone || '',
            showButton: !!trucker.phone,
          }}
          onCallbackCopyPhone={(phone) => {
            amplitudeEvent('drawer_proposal_telephone_copied', {
              source: 'copiar-telefone-drawer-proposta-pendente',
              proposal_id: proposalsSelection?.id,
              trucker_id: trucker?.id || '',
              numero_copiado: phone,
            });
          }}
          onCallbackSentWhatsApp={() => {
            amplitudeEvent('drawer_proposal_whatsapp_for_trucker_sent', {
              source: 'drawer-proposta-pendente',
              proposal_id: proposalsSelection?.id,
              trucker_id: trucker?.id || '',
            });
          }}
          depositFee={trucker?.depositFee}
          journeyId={proposalsSelection?.id}
          showIn="proposal"
        />
      </Styled.Section>
    );
  }

  function handleTruckerDepositFee(loading: boolean) {
    if (loading && trucker?.depositFee?.tagLabel) {
      return (
        <Styled.Section>
          <FuelSkeleton
            shape="square"
            size="custom"
            width={!isMobile ? '462px' : '360px'}
            height="52px"
          />
        </Styled.Section>
      );
    }
    return (
      trucker?.depositFee.tagLabel &&
      trucker?.depositFee?.disclaimer?.description && (
        <Styled.Section>
          <FuelCallout
            icon={trucker?.depositFee?.disclaimer?.icon || undefined}
            iconStyle="filled"
            fullWidth
            color="blue"
          >
            <FuelTypography slot="content" size="caption" weight="medium">
              {trucker?.depositFee?.disclaimer?.description}
              {trucker?.depositFee?.disclaimer?.link && (
                <FuelTypography displayInline spacingLeft={5} size="caption">
                  <Styled.WrapperLink
                    data-testid="link-landing-page"
                    onClick={() => {
                      pageLinkOpen(trucker?.depositFee?.disclaimer?.link);
                      amplitudeEvent(
                        'drawer_proposal_know_more_for_landing_page',
                        {
                          source: 'drawer-proposta-saiba-mais',
                          proposal_id: proposalsSelection?.id,
                          trucker_id: trucker?.id || '',
                        },
                      );
                    }}
                  >
                    Saiba mais
                  </Styled.WrapperLink>
                </FuelTypography>
              )}
            </FuelTypography>
          </FuelCallout>
        </Styled.Section>
      )
    );
  }

  function handleOriginAndDestination(loading: boolean) {
    if (loading) {
      return (
        <Styled.Section>
          <FuelFlex defaultProps={{ gap: 'km3' }}>
            <SkeletonOriginAndDestination />
            <FuelFlex defaultProps={{ flexDirection: 'column', gap: 'km3' }}>
              <FuelSkeleton
                shape="pill"
                size="custom"
                width="89px"
                height="16px"
              />
              <FuelSkeleton
                shape="pill"
                size="custom"
                width="135px"
                height="16px"
              />
            </FuelFlex>
          </FuelFlex>
        </Styled.Section>
      );
    }
    return (
      <Styled.Section>
        <FuelOriginAndDestination
          withCaption
          origin={proposalsSelection?.origin}
          destination={proposalsSelection?.destination}
          isLarge={false}
        />
      </Styled.Section>
    );
  }

  useEffect(() => {
    if (isShow) {
      addTimeout(() => {
        baseRef.current?.classList.add('animated');
        contentRef.current?.classList.add('animated');
        actionRef.current?.classList.add('animated');
      }, 200);
      fetchProposal();
    }
  }, [isShow]);

  useEffect(() => {
    window.addEventListener('proposalConfirmedSuccess', handleClose);

    return () => {
      window.removeEventListener('proposalConfirmedSuccess', handleClose);
    };
  }, []);

  return (
    <BaseModal
      isOpen={isShow}
      onClose={() => handleClose()}
      innerRef={baseRef}
      vAlignment="top"
      hAlignment="right"
      hOverlay
    >
      <Styled.Content isShow={isShow} ref={contentRef}>
        <FuelFlex defaultProps={{ alignItems: 'flex-start', gap: 'km6' }}>
          <FuelButton
            variant="secondary"
            size="medium"
            leadingIcon="close"
            onlyIcon
            data-testid="DrawerProposalCloseBtn"
            onClick={() => handleClose()}
          />
          <div>
            <FuelTypography
              size="heading-md"
              weight="bold"
              color="neutral-strong"
              spacingTop={0}
              spacingBottom={0}
              spacingRight={8}
            >
              Viagem pendente de confirmação
            </FuelTypography>
          </div>
        </FuelFlex>
        {handleTruckerInfo(isloading)}
        {handleTruckerDepositFee(isloading)}
        <Styled.Section gap="km2">
          <LastLocation
            isLoading={isloading}
            location={{
              cityName: proposalsSelection?.trucker.lastLocation.city_name,
              stateAcronym:
                proposalsSelection?.trucker.lastLocation.state_acronym,
              updatedAt: proposalsSelection?.trucker.lastLocation.timestamp,
            }}
            onClickReload={() => {
              if (proposalId) {
                fetchProposal();
                amplitudeEvent('proposal_drawer_refresh_clicked', {
                  source: 'recarregar-acompanhamento-viagem',
                  proposal_id: proposalId,
                });
              }
            }}
          />
        </Styled.Section>
        {handleOriginAndDestination(isloading)}
        <Actions
          innerRef={actionRef}
          proposalId={proposalId}
          createdBy={proposalsSelection?.created_by}
        />
      </Styled.Content>
    </BaseModal>
  );
}

export default DrawerProposal;
